import { useMemo } from "react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";

import type { useMatchFlow } from "@/features/match/useMatchFlow";
import { useTranslations } from "@/i18n";
import { usePlayers } from "@/store/selectors";
import { isFreeForAll, isTeams } from "@/types";
import { TEAM_COLORS, type TeamColorKey, theme } from "@/ui/theme";

// ── Helpers ──────────────────────────────────────────────────

const TEAM_COLOR_ORDER: TeamColorKey[] = ["team-a", "team-b", "team-c", "team-d"];

const MEDALS: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

// ── Styled ───────────────────────────────────────────────────

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const StageTag = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${theme.colors.textMuted};
`;

const Title = styled.h2`
  font-family: ${theme.fonts.display};
  font-size: 1.875rem;
  line-height: 1.1;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TeamCardEl = styled.button<{
  teamColor: string;
  rank: number;
  hasReachedK?: boolean;
}>`
  all: unset;
  cursor: ${(p) => (p.hasReachedK ? "default" : "pointer")};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid
    color-mix(in oklab, ${(p) => p.teamColor} ${(p) => (p.rank > 0 ? "50%" : "30%")}, transparent);
  background: ${(p) =>
    p.rank > 0
      ? `color-mix(in oklab, ${p.teamColor} 12%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bgCard} 70%, transparent)`};
  box-shadow: ${theme.shadow.card};
  opacity: ${(p) => (p.hasReachedK ? 0.45 : p.rank > 0 ? 0.7 : 1)};
  transition: all 0.15s;
  text-align: left;
  -webkit-tap-highlight-color: transparent;

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: ${theme.radii.xl};
  font-size: 1.25rem;
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bg} 60%, transparent);
`;

const TeamBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const TeamLabel = styled.div`
  font-family: ${theme.fonts.display};
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TeamPlayers = styled.div`
  font-size: 0.75rem;
  color: color-mix(in oklab, ${theme.colors.text} 70%, transparent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RankLabel = styled.span<{ teamColor: string }>`
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.125rem 0.5rem;
  border-radius: ${theme.radii.full};
  background: color-mix(in oklab, ${(p) => p.teamColor} 25%, transparent);
  color: ${(p) => p.teamColor};
`;

const SummaryBar = styled.div`
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
`;

// ── Component ────────────────────────────────────────────────

type FlowApi = ReturnType<typeof useMatchFlow>;

export const PlayStage = ({ flow }: { flow: FlowApi }) => {
  const { t } = useTranslations();
  const playersById = usePlayers();
  const { activity, teams, nonEmptyTeamIndices, rankEntries, rankSummary, assignRank } = flow;

  // topK constraint
  let topK = 0;
  if (activity && (isFreeForAll(activity) || isTeams(activity))) {
    topK = activity.topK ?? 0;
  }
  const kLimit = topK > 0;
  const kReached = kLimit && rankEntries.length >= topK;

  const teamMeta = useMemo(() => {
    const result: Record<number, { label: string; names: string; colorKey: TeamColorKey }> = {};
    for (const i of nonEmptyTeamIndices) {
      const team = teams[i]!;
      const names = team.map((pid) => playersById[pid]?.name ?? "?").join(", ");
      const isSolo = team.length === 1;
      result[i] = {
        label: isSolo ? (playersById[team[0]!]?.name ?? "?") : t("general.teamN", i + 1),
        names,
        colorKey: TEAM_COLOR_ORDER[i % TEAM_COLOR_ORDER.length]!,
      };
    }
    return result;
  }, [nonEmptyTeamIndices, teams, playersById]);

  if (!activity) return null;

  return (
    <Content>
      <header>
        <StageTag>{t("arena.stagePodium", isTeams(activity) ? 3 : 2)}</StageTag>
        <Title>{t("arena.whoTookIt")}</Title>
        <Subtitle>{t("arena.podiumSubtitle")}</Subtitle>
      </header>

      <TeamList>
        <AnimatePresence>
          {nonEmptyTeamIndices.map((origIdx) => {
            const meta = teamMeta[origIdx]!;
            const rank = flow.rankOf(origIdx);
            const color = TEAM_COLORS[meta.colorKey];
            const disabled = kReached && rank === 0;
            const medal = rank > 0 ? (MEDALS[rank] ?? `#${rank}`) : "";

            return (
              <motion.div
                key={origIdx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ type: "spring", stiffness: 300, damping: 28 }}
                style={{ display: "flex" }}
              >
                <TeamCardEl
                  teamColor={color}
                  rank={rank}
                  hasReachedK={disabled}
                  onClick={() => {
                    if (disabled) return;

                    assignRank(origIdx, false);
                  }}
                  onDoubleClick={(e) => {
                    e.preventDefault();

                    if (rank === 0) assignRank(origIdx, true);
                  }}
                >
                  <RankBadge rank={rank}>{rank > 0 ? medal : "-"}</RankBadge>
                  <TeamBody>
                    <TeamLabel>
                      {meta.label}
                      {rank > 0 && <RankLabel teamColor={color}>#{rank}</RankLabel>}
                    </TeamLabel>
                    {teams[origIdx]!.length > 1 && <TeamPlayers>{meta.names}</TeamPlayers>}
                  </TeamBody>
                </TeamCardEl>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </TeamList>

      {/* Rank summary */}
      {rankSummary.length > 0 && (
        <SummaryBar>
          {rankSummary.map(([rank, teamIdxs]) => {
            const isTied = teamIdxs.length > 1;
            const names = teamIdxs
              .map((ti) => teamMeta[ti]?.label ?? "?")
              .join(isTied ? " = " : ", ");
            return (
              <span key={rank}>
                <strong>#{rank}</strong> {names}
                {isTied && ` ${t("arena.tied")}`}
              </span>
            );
          })}
        </SummaryBar>
      )}
    </Content>
  );
};
